import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Minus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import api from "../../lib/api";
import { FlashCountdown } from "./FlashCountdown";

export const BADGE = "/img/noleggio/badge.webp";
let req = null;
const EMPTY = { enabled: false, preview: false, loaded: false, bundles: [], registers: [], months: [], flash: null };
export const useNoleggio = () => {
  const [d, setD] = useState(EMPTY);
  useEffect(() => {
    req = req || api.get("/public/noleggio").then(({ data }) => data).catch(() => { req = null; return EMPTY; });
    let alive = true;
    req.then((x) => alive && setD({ ...EMPTY, ...x, loaded: true }));
    return () => { alive = false; };
  }, []);
  return d;
};
export const useRegisters = () => useNoleggio().registers;

const pick = (not, n) => { let x; do { x = Math.floor(Math.random() * n); } while (x === not && n > 1); return x; };

export const NoleggioRotator = ({ dark = false, compact = false }) => {
  const all = useRegisters();
  const feat = all.filter((r) => r.featured);
  const regs = feat.length ? feat : all;
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!regs.length) return undefined;
    setI(pick(-1, regs.length));
    const t = setInterval(() => setI((x) => pick(x, regs.length)), 2600);
    return () => clearInterval(t);
  }, [regs.length]);
  const r = regs[i];
  return (
    <div className={`relative flex flex-col items-center justify-center ${compact ? "h-[210px]" : "h-[300px] lg:h-[360px]"}`} data-testid="noleggio-rotator">
      <div className={`absolute rounded-full blur-2xl ${compact ? "w-[180px] h-[180px]" : "w-[280px] h-[280px]"} ${dark ? "bg-[#CF1072]/35" : "bg-[#CF1072]/15"}`} />
      <div className={`absolute rounded-full border-2 border-dashed es-noleggio-spin ${compact ? "w-[200px] h-[200px]" : "w-[300px] h-[300px] lg:w-[340px] lg:h-[340px]"} ${dark ? "border-white/25" : "border-[#1A237E]/20"}`} />
      {r && <Link to={`/prodotti/${encodeURIComponent(r.slug)}`} className="relative flex flex-col items-center" data-testid="noleggio-rotator-link">
      <img key={i} src={r.img} alt={r.name} className={`relative object-contain drop-shadow-2xl es-noleggio-in ${compact ? "max-h-[160px] max-w-[200px]" : "max-h-[250px] lg:max-h-[290px] max-w-[300px]"}`} data-testid="noleggio-rotator-img" />
      <span key={`l-${i}`} className={`relative mt-2 text-[11px] font-bold uppercase tracking-[0.18em] es-noleggio-in ${dark ? "text-white/75" : "text-[#1A237E]"}`} data-testid="noleggio-rotator-name">{r.name} · {r.type}</span>
      </Link>}
    </div>
  );
};

export const NoleggioMarquee = ({ dark = false }) => {
  const regs = useRegisters();
  if (!regs.length) return null;
  return (
    <div className="overflow-hidden es-marquee-mask" data-testid="noleggio-marquee">
      <div className="flex w-max gap-6 es-marquee">
        {[...regs, ...regs].map((r, k) => (
          <Link key={k} to={`/prodotti/${encodeURIComponent(r.slug)}`} className={`w-[130px] shrink-0 rounded-2xl p-3 flex flex-col items-center hover:-translate-y-1 transition-transform duration-200 ${dark ? "bg-white/10" : "bg-white border border-[#e6e8f2]"}`} data-testid={k < regs.length ? `noleggio-marquee-item-${r.slug}` : undefined}>
            <img src={r.img} alt={r.name} loading="lazy" className="h-[80px] object-contain" />
            <p className={`text-[11px] font-bold mt-1 truncate max-w-full ${dark ? "text-white" : "text-[#1A237E]"}`}>{r.name}</p>
            <p className={`text-[10px] ${dark ? "text-white/60" : "text-[#56585e]"}`}>{r.type}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const NoleggioTitle = ({ className = "", as: H = "h2" }) => (
  <H className={`font-bold leading-[1.05] ${className}`} data-testid="noleggio-title">
    <span className="text-[#CF1072]">Easy Noleggio</span> con Easy System
  </H>
);

export const EasyNoleggioFab = () => {
  const { pathname } = useLocation();
  const { bundles, enabled, flash } = useNoleggio();
  const [flashOn, setFlashOn] = useState(true);
  const endFlash = React.useCallback(() => setFlashOn(false), []);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("es_nol_banner_closed") === "1");
  const [show, setShow] = useState(false);
  const [mini, setMini] = useState(() => { const v = sessionStorage.getItem("es_nol_banner_mini"); return v ? v === "1" : localStorage.getItem("es_nol_seen") === "1"; });
  useEffect(() => { if (show) localStorage.setItem("es_nol_seen", "1"); }, [show]);
  useEffect(() => {
    if (mini || window.innerWidth >= 640) return undefined;
    const on = () => { if (window.scrollY > 500) { setMini(true); sessionStorage.setItem("es_nol_banner_mini", "1"); } };
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [mini]);
  const { registers } = useNoleggio();
  const [i, setI] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShow(true), 2500); return () => clearTimeout(t); }, []);
  useEffect(() => { if (registers.length < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % registers.length), 2800); return () => clearInterval(t); }, [registers.length]);
  if (!enabled || pathname.startsWith("/easy-noleggio") || pathname.startsWith("/admin") || pathname.startsWith("/tecnici")) return null;
  const close = (e) => { e.stopPropagation(); sessionStorage.setItem("es_nol_banner_closed", "1"); setHidden(true); };
  const minimize = (v) => (e) => { e.stopPropagation(); sessionStorage.setItem("es_nol_banner_mini", v ? "1" : "0"); setMini(v); };
  const live = flashOn && flash && new Date(flash.ends_at) > new Date() ? flash : null;
  const r = live?.product || registers[i];
  const own = r && Math.min(...Object.values(registers.find((x) => x.product_id === r.product_id)?.rates || {}));
  const from = Number.isFinite(own) ? own : null;
  return (
    <>
      {!hidden && show && mini && (
        <button onClick={minimize(false)} className="fixed bottom-5 left-4 z-40 w-16 h-16 rounded-2xl overflow-hidden shadow-[0_18px_40px_-12px_rgba(13,20,66,0.7)] ring-2 ring-[#CF1072] es-noleggio-glow hover:scale-105 transition-transform duration-200" aria-label="Espandi banner Easy Noleggio" data-testid="noleggio-fab-bubble"><img src={BADGE} alt="Easy Noleggio" className="w-full h-full object-cover" /></button>
      )}
      {!hidden && show && !mini && (
        <div role="button" tabIndex={0} onClick={() => setOpen(true)} onKeyDown={(e) => e.key === "Enter" && setOpen(true)} className="fixed bottom-5 left-4 z-40 w-[calc(100vw-2rem)] max-w-[420px] cursor-pointer rounded-3xl overflow-hidden shadow-[0_24px_60px_-18px_rgba(13,20,66,0.65)] border border-white/10 bg-[#0d1442] text-white es-noleggio-toast hover:-translate-y-1 transition-transform duration-300" aria-label="Apri Easy Noleggio" data-testid="noleggio-fab">
          <div className="absolute top-2 right-2 z-10 flex gap-1.5">
            <button onClick={minimize(true)} className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center" aria-label="Riduci banner Easy Noleggio" title="Riduci" data-testid="noleggio-fab-minimize"><Minus className="w-4 h-4" /></button>
            <button onClick={close} className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center" aria-label="Chiudi banner Easy Noleggio" title="Chiudi" data-testid="noleggio-fab-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="relative h-[110px] sm:h-[150px] bg-cover bg-center" style={{ backgroundImage: "url(/img/noleggio/banner.webp)" }}>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d1442] via-[#0d1442]/60 to-transparent" />
            <img src={BADGE} alt="" className="absolute left-3 top-3 w-12 h-12 rounded-xl shadow-lg" />
            <span className="absolute left-3 bottom-3 h-6 px-2.5 rounded-full bg-[#CF1072] text-[10px] font-bold uppercase tracking-wider flex items-center es-noleggio-glow">Novità</span>
            {r && <img key={live?.product ? "flash" : i} src={r.img} alt={r.name} className="absolute right-12 bottom-1 h-[100px] sm:h-[140px] max-w-[150px] sm:max-w-[190px] object-contain drop-shadow-2xl es-noleggio-in" data-testid="noleggio-fab-img" />}
          </div>
          {live && (
            <div className="px-4 sm:px-5 py-3 bg-gradient-to-r from-[#CF1072] to-[#8e0b4f] flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="noleggio-fab-flash">
              <div className="flex-1 min-w-0 text-left"><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">Offerta lampo · scade tra</p><p className="text-[14px] font-bold leading-tight line-clamp-2" data-testid="noleggio-fab-flash-text">{live.text}</p></div>
              <FlashCountdown end={live.ends_at} onEnd={endFlash} />
            </div>
          )}
          <div className="p-4 sm:p-5 text-center">
            <p className="text-[19px] sm:text-[22px] font-bold leading-tight"><span className="text-[#ff7bbd]">Easy Noleggio</span> con Easy System</p>
            <p className="text-[14px] text-white/75 mt-1">{r ? `${r.name} · ${r.type}` : "Registratori di cassa a canone"}{from ? ` · da ${from.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}/mese` : ""}</p>
            <span className="mt-4 h-11 w-full rounded-full bg-[#CF1072] text-[15px] font-bold flex items-center justify-center gap-1.5" data-testid="noleggio-fab-cta">Scopri le offerte <ArrowRight className="w-4 h-4" /></span>
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px] p-0 overflow-hidden border-0 bg-[#1A237E] [&>button]:hidden" data-testid="noleggio-dialog">
          <div className="relative p-6 pb-2 text-white">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25" aria-label="Chiudi" data-testid="noleggio-dialog-close"><X className="w-4 h-4" /></button>
            <div className="flex items-center gap-3"><img src={BADGE} alt="Easy Noleggio" className="w-12 h-12 rounded-[12px]" /><DialogTitle className="text-[24px] font-bold leading-[1.05] text-white" data-testid="noleggio-dialog-title"><span className="text-[#ff7bbd]">Easy Noleggio</span> con Easy System</DialogTitle></div>
            <p className="text-[13px] text-white/80 mt-2">Registratori di cassa classici e touch a canone mensile: installazione, software e assistenza inclusi.</p>
            <NoleggioRotator dark compact />
          </div>
          <div className="bg-white p-5 space-y-3">
            <div className="flex flex-wrap gap-2 text-[12px] font-bold text-[#1A237E]">{bundles.map((b) => <span key={b.id} className="px-3 h-7 rounded-full bg-[#f4f6fc] flex items-center">{b.name}</span>)}</div>
            <Link to="/easy-noleggio" onClick={() => setOpen(false)} className="es-btn-primary h-11 w-full flex items-center justify-center gap-2" data-testid="noleggio-dialog-cta">Scopri Easy Noleggio <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EasyNoleggioSection = () => {
  const { enabled } = useNoleggio();
  return enabled ? <EasyNoleggioHome /> : null;
};

const EasyNoleggioHome = () => (
  <section className="relative overflow-hidden bg-[#0d1442]" data-testid="noleggio-home-section">
    <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: "url(/img/noleggio/banner.webp)" }} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0d1442] via-[#0d1442]/85 to-[#0d1442]/30" />
    <div className="relative max-w-[1224px] mx-auto px-4 lg:px-5 py-14 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
      <div className="es-reveal">
        <img src={BADGE} alt="Easy Noleggio" className="w-16 h-16 rounded-[16px] shadow-2xl" />
        <NoleggioTitle className="text-4xl sm:text-5xl text-white mt-4" />
        <p className="text-white/80 text-base md:text-lg mt-4 max-w-[540px]">Il registratore di cassa giusto, classico o touch, a un canone fisso. Niente anticipo, tutto incluso.</p>
        <Link to="/easy-noleggio" className="inline-flex items-center gap-2 mt-6 es-btn-primary h-[48px] px-6 text-[15px]" data-testid="noleggio-home-cta">Scopri i bundle <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <NoleggioRotator dark />
    </div>
  </section>
);
